import { useQuery } from "@tanstack/react-query";

import { HouseholdMembersApi } from "../api/householdMembersApi";
import { useCurrentPatientQuery } from "./patientsQueries";

export const HouseholdMembersQueryKeys = {
  all: ["householdMembers"] as const,
  me: () => [...HouseholdMembersQueryKeys.all, "me"] as const,
  list: (shouldIncludeMe?: boolean) =>
    shouldIncludeMe !== undefined
      ? ([
          ...HouseholdMembersQueryKeys.all,
          "list",
          { shouldIncludeMe },
        ] as const)
      : ([...HouseholdMembersQueryKeys.all, "list"] as const),
  details: (id: string) =>
    [...HouseholdMembersQueryKeys.all, "details", id] as const,
};

export const useListHouseholdMembersQuery = (shouldIncludeMe = true) => {
  const { data: currentPatient } = useCurrentPatientQuery();

  return useQuery({
    queryKey: HouseholdMembersQueryKeys.list(shouldIncludeMe),
    queryFn: async () => {
      const members = await HouseholdMembersApi.listHouseholdMembers();

      if (!shouldIncludeMe) {
        return members?.filter(
          member => member.patientId !== currentPatient?.id,
        );
      }

      return members;
    },
    refetchInterval: query =>
      query.state.data?.some(member => member.coverage?.status === "CHECKING")
        ? 5_000
        : false,
  });
};

export const useGetHouseholdMemberQuery = (id: string) => {
  return useQuery({
    queryKey: HouseholdMembersQueryKeys.details(id),
    queryFn: () => HouseholdMembersApi.getHouseholdMember(id),
    enabled: Boolean(id),
  });
};

export const useCurrentHouseholdMemberQuery = () => {
  return useQuery({
    queryKey: HouseholdMembersQueryKeys.me(),
    queryFn: () => HouseholdMembersApi.getCurrentHouseholdMember(),
  });
};
